<script lang="ts">
  import { npubEncode } from 'nostr-tools/nip19';
  import type { BadgeAward, BitcoinPlusPlusSession, PublicConfig } from './api';
  import { credentialStatus, getBitcoinPlusPlusSession, getPublicAward, recordBadgeAcceptance } from './api';
  import { acceptBadge, republishProfileBadge, type ProfilePublishResult } from './profileBadges';
  import BitcoinWordmark from './BitcoinWordmark.svelte';
  import PeacockBadge from './PeacockBadge.svelte';
  import RecipientSigner from './RecipientSigner.svelte';
  import type { SignerConnection } from './signers';

  let { eventID, config }: { eventID: string; config: PublicConfig } = $props();
  let award = $state<BadgeAward | null>(null);
  let loading = $state(true);
  let error = $state('');
  let result = $state<ProfilePublishResult | null>(null);
  let accepting = $state(false);
  let recipientPubkey = $state('');
  let bitcoinPlusPlusSession = $state<BitcoinPlusPlusSession | null>(null);
	const recordedAcceptance = $derived(award?.recipients.length === 1 ? award.acceptances?.[award.recipients[0]] : undefined);

  $effect(() => {
    getPublicAward(eventID).then((value) => award = value.award).catch((cause) => error = cause instanceof Error ? cause.message : 'Could not load this award.').finally(() => loading = false);
    if (config.bitcoinplusplus_enabled) void getBitcoinPlusPlusSession().then((value) => bitcoinPlusPlusSession = value);
  });

  async function connected(connection: SignerConnection) {
    if (!award) return;
    if (credentialStatus(award, connection.pubkey) !== 'active') { error = 'This credential is expired or revoked.'; return; }
    recipientPubkey = connection.pubkey;
    accepting = true;
    error = '';
    try {
      result = await acceptBadge(connection, award, config.relays);
      if (result.event) {
        try {
          await recordBadgeAcceptance(award.event_id, result.event);
        } catch {
          // The signed profile event is the credential acceptance. The studio
          // receipt is reconciliation metadata and must not turn success into
          // a false failure when Bitcoin++ is temporarily unavailable.
        }
      }
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not accept this badge.';
    } finally {
      accepting = false;
      await connection.close();
    }
  }

  async function retryFailedRelays() {
    if (!result?.event || result.failedRelays.length === 0) return;
    accepting = true;
    error = '';
    try {
      const retried = await republishProfileBadge(result.event, result.failedRelays);
      result = {
        ...result,
        acceptedRelays: [...new Set([...result.acceptedRelays, ...retried.acceptedRelays])],
        failedRelays: retried.failedRelays
      };
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not retry the remaining relays.';
    } finally {
      accepting = false;
    }
  }

  function shortIssuer(pubkey: string): string {
    const npub = npubEncode(pubkey);
    return `${npub.slice(0, 15)}…${npub.slice(-7)}`;
  }

  function hideBrokenArtwork(event: Event) {
    (event.currentTarget as HTMLImageElement).hidden = true;
  }
</script>

<main class="claim-page">
  <a class="claim-brand" href="/" aria-label="Badge Studio by Bitcoin++ home"><BitcoinWordmark /><span>Badge Studio</span></a>
  {#if loading}
    <div class="loading"><PeacockBadge /><p>Verifying your award…</p></div>
  {:else if !award || !award.badge}
    <div class="not-found"><span>404</span><h1>This feather could not be found.</h1><p>{error || 'Check the award link and try again.'}</p></div>
  {:else}
    <section class="claim-card">
      <div class="claim-art" style={`--badge-image: url("${award.badge.image_url.replaceAll('"', '%22')}")`}>
        <b aria-hidden="true">{award.badge.name.trim().charAt(0).toUpperCase() || '+'}</b>
        <img src={award.badge.image_url} alt={`Artwork for ${award.badge.name}`} onerror={hideBrokenArtwork} />
      </div>
      <div class="claim-copy">
        {#if award.revocation}
          <p class="eyebrow">award revoked</p>
          <h1>No longer valid.</h1>
          <p class="lede">The issuing organization revoked this award on {new Date(award.revocation.created_at).toLocaleDateString(undefined, { dateStyle: 'long' })}.</p>
          {#if award.revocation.reason}<p class="revocation-reason">{award.revocation.reason}</p>{/if}
        {:else if award.recipients.length === 1 && credentialStatus(award,award.recipients[0]) === 'expired'}
          <h1>This membership has expired.</h1><p class="lede">Renewal extends the same credential. The historical award remains available.</p><a href={`/credentials/${eventID}/${award.recipients[0]}`}>View credential</a>
        {:else if award.recipients.length === 1 && credentialStatus(award,award.recipients[0]) === 'expired'}
          <h1>This membership has expired.</h1><p class="lede">Renewal extends the same credential. The historical award remains available.</p><a href={`/credentials/${eventID}/${award.recipients[0]}`}>View credential</a>
        {:else if result || recordedAcceptance}
          <div class="accepted-mark"><svg viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="32" r="27"/><path d="M18 33l9 9 20-23"/></svg></div>
          <p class="eyebrow">{result?.alreadyAccepted || recordedAcceptance ? 'already in your profile' : 'badge accepted'}</p>
          <h1>Wear it proudly.</h1>
          <p class="lede">{result && !result.alreadyAccepted ? `${result.acceptedRelays.length} relays accepted your updated profile badge list.` : 'This badge is already part of the recipient’s Nostr profile.'}</p>
			<div class="accepted-actions"><a class="credential-link" href={`/credentials/${award.event_id}/${recipientPubkey || award.recipients[0]}`}>View portable credential <span>→</span></a>{#if bitcoinPlusPlusSession && config.bitcoinplusplus_url}<a class="credential-link" href={`${config.bitcoinplusplus_url}/dashboard/orgs#badge-inbox`}>Return to bitcoin++ <span>↗</span></a>{/if}</div>
          {#if result?.failedRelays.length}<div class="relay-note"><p>{result.failedRelays.length} {result.failedRelays.length === 1 ? 'relay did' : 'relays did'} not respond. Your successful publications remain valid.</p><button type="button" onclick={retryFailedRelays} disabled={accepting}>{accepting ? 'Retrying…' : 'Retry remaining relays'}</button></div>{/if}
        {:else}
          <p class="eyebrow">you’ve been recognized</p>
          <h1>{award.badge.name}</h1>
          <p class="lede">{award.badge.description}</p>
          <dl><div><dt>Issued by</dt><dd>{shortIssuer(award.issuer_pubkey)}</dd></div><div><dt>Awarded</dt><dd>{new Date(award.created_at).toLocaleDateString(undefined, { dateStyle: 'long' })}</dd></div></dl>
          <div class="accept-panel">
            <h2>{accepting ? 'Check your signer…' : 'Add it to your Nostr profile'}</h2>
            <p>This adds the badge to your public profile list without changing the badges already there.</p>
            {#if !accepting}<RecipientSigner relays={config.signer_relays} managedSignerURL={config.managed_signer_url ?? ''} bitcoinPlusPlusPersonID={bitcoinPlusPlusSession?.person_id ?? ''} bitcoinPlusPlusEnabled={config.bitcoinplusplus_enabled} returnPath={`/claim/${eventID}`} expectedPubkey={award.recipients[0] ?? ''} onconnected={connected} />{:else}<div class="accepting"><i></i><span>Waiting for your signature</span></div>{/if}
          </div>
          {#if error}<p class="error" role="alert">{error}</p>{/if}
        {/if}
      </div>
    </section>
  {/if}
</main>

<style>
  .claim-page { width: min(1180px, calc(100% - 48px)); min-height: 100dvh; margin: auto; padding: 30px 0 60px; }
  .claim-brand { --wordmark-size: 27px; display: inline-flex; align-items: center; gap: 12px; color: var(--ink); text-decoration: none; }
  .claim-brand > span { padding-left: 12px; border-left: 1px solid var(--line-strong); font-size: 12px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
  .claim-card { display: grid; grid-template-columns: minmax(340px, .9fr) minmax(430px, 1.1fr); min-height: 670px; margin-top: 28px; border: 1px solid var(--line-strong); background: var(--surface); box-shadow: 20px 24px 0 color-mix(in srgb, var(--violet) 13%, transparent); }
  .claim-art { position: relative; display: grid; place-items: center; min-height: 560px; overflow: hidden; background: linear-gradient(145deg, rgba(20,96,116,.92), rgba(64,38,111,.92)), var(--badge-image) center/cover; }
  .claim-art::before, .claim-art::after { position: absolute; width: 80%; aspect-ratio: 1; border: 1px solid rgba(255,255,255,.25); border-radius: 50%; content: ''; animation: orbit 18s linear infinite; }
  .claim-art::after { width: 60%; animation-direction: reverse; }
  .claim-art > b, .claim-art img { z-index: 1; grid-area: 1 / 1; width: min(68%, 390px); aspect-ratio: 1; border: 12px solid rgba(249,240,220,.92); border-radius: 50%; box-shadow: 0 25px 65px rgba(10,15,30,.34); }
  .claim-art > b { display: grid; place-items: center; background: rgba(249,240,220,.12); color: rgba(249,240,220,.94); font: 500 clamp(72px, 13vw, 150px)/1 var(--display); }
  .claim-art img { object-fit: cover; }
  .claim-copy { padding: clamp(38px, 6vw, 76px); }
  .eyebrow { margin: 0 0 17px; color: var(--violet); font-size: 10px; font-weight: 850; letter-spacing: .15em; }
  h1 { margin: 0; font-family: var(--display); font-size: clamp(50px, 7vw, 78px); font-weight: 500; letter-spacing: -.055em; line-height: .92; }
  .lede { margin: 22px 0 28px; color: var(--muted); font-size: 17px; line-height: 1.55; }
  dl { margin: 0; border-top: 1px solid var(--line); }
  dl div { display: grid; grid-template-columns: 90px 1fr; gap: 13px; padding: 12px 0; border-bottom: 1px solid var(--line); }
  dt { color: var(--muted); font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  dd { margin: 0; overflow: hidden; font: 10px var(--mono); text-overflow: ellipsis; }
  .accept-panel { margin-top: 34px; }
  .accept-panel h2 { margin: 0; font-family: var(--display); font-size: 25px; font-weight: 500; }
  .accept-panel > p { margin: 7px 0 19px; color: var(--muted); font-size: 13px; line-height: 1.5; }
  .accepting { display: flex; align-items: center; gap: 12px; min-height: 110px; color: var(--muted); font-size: 12px; }
  .accepting i { width: 10px; height: 10px; border-radius: 50%; background: var(--aqua); box-shadow: 0 0 0 6px color-mix(in srgb, var(--aqua) 15%, transparent); animation: pulse 1.2s infinite; }
  .accepted-mark svg { width: 80px; fill: color-mix(in srgb, var(--aqua) 14%, transparent); stroke: var(--teal); stroke-width: 4; stroke-linecap: round; stroke-linejoin: round; }
  .credential-link { display: inline-flex; align-items: center; gap: 12px; min-height: 44px; margin: 0 0 24px; border-bottom: 1px solid var(--ink); color: var(--ink); font-size: 12px; font-weight: 850; text-decoration: none; }
	.accepted-actions { display: flex; flex-wrap: wrap; gap: 18px; }
  .relay-note, .error { padding: 11px 13px; font-size: 13px; }
  .relay-note { display: flex; align-items: center; justify-content: space-between; gap: 14px; background: #fff3d1; color: #72540c; }
  .relay-note p { margin: 0; line-height: 1.45; }
  .relay-note button { flex: none; min-height: 40px; padding: 8px 13px; border: 1px solid currentColor; border-radius: 999px; background: transparent; color: inherit; font-weight: 750; cursor: pointer; }
  .error { border-left: 3px solid var(--coral); background: #f9e4e4; color: #842f38; }
  .revocation-reason { padding: 14px; border-left: 3px solid var(--coral); background: #f9e4e4; color: #842f38; line-height: 1.5; }
  .loading { width: min(340px, 80vw); margin: 100px auto; text-align: center; }
  .loading p { color: var(--muted); }
  .not-found { max-width: 700px; margin: 100px 0; }
  .not-found > span { color: var(--aqua); font: italic 76px var(--display); }
  .not-found p { color: var(--muted); }
  @keyframes orbit { to { transform: rotate(360deg); } }
  @keyframes pulse { 50% { opacity: .4; transform: scale(.8); } }
  @media (max-width: 780px) { .claim-page { width: calc(100% - 24px); padding-top: 16px; } .claim-card { grid-template-columns: 1fr; min-height: 0; box-shadow: 10px 13px 0 color-mix(in srgb, var(--violet) 13%, transparent); } .claim-art { min-height: 360px; } .claim-art > b, .claim-art img { width: min(64%, 280px); border-width: 8px; } .claim-copy { padding: 38px var(--mobile-gutter) 48px; } }
  @media (max-width: 400px) { .claim-art { min-height: 310px; } h1 { font-size: 48px; } dl div { grid-template-columns: 72px 1fr; } .relay-note { align-items: stretch; flex-direction: column; } }
  @media (prefers-reduced-motion: reduce) { .claim-art::before, .claim-art::after, .accepting i { animation: none; } }
</style>
